import type { Bom, BomUpsert, Item } from '@erp/shared';
import { Factory, ScanLine, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Card, Skeleton } from '../../components/shadcn/index.js';
import { CurrencySelect } from '../../components/ui/CurrencySelect.js';
import {
  Button, Combobox, type ComboboxOption, confirmDialog, FormField, Input, NumberInput,
} from '../../components/ui/index.js';
import { api } from '../../lib/api.js';
import { trimMoney } from '../../lib/money.js';
import { normalizeNumericInput, safeDecimal } from '../../lib/numerals.js';

/** One recipe row — display fields snapshotted at add time (transfer-form pattern). */
interface RecipeLine {
  _key: string;
  componentItemId: string;
  code: string;
  name: string;
  uom: string;
  /** Per BATCH, in base units. */
  quantity: string;
}

let keySeq = 0;
const nextKey = (): string => `rl-${++keySeq}`;
const SEARCH_DEBOUNCE_MS = 250;
const SEARCH_LIMIT = 30;

interface Props {
  /** The FINISHED item this recipe belongs to. */
  itemId: string;
  itemUom: string;
  canWrite: boolean;
}

/**
 * The item's Recipe (BOM) tab — the ONE editor for what this product is made
 * of. Saving replaces the whole recipe server-side; posted production orders
 * are untouched (they carry their own line snapshots). Components are ordinary
 * items found by the same server search every document form uses.
 */
export function ItemRecipeTab({ itemId, itemUom, canWrite }: Props): JSX.Element {
  const { t } = useTranslation('manufacturing');
  const [loaded, setLoaded] = useState(false);
  const [exists, setExists] = useState(false);
  const [outputQty, setOutputQty] = useState('1');
  const [extraCost, setExtraCost] = useState('0');
  const [extraCcy, setExtraCcy] = useState('');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<RecipeLine[]>([]);
  const [saving, setSaving] = useState(false);
  const [term, setTerm] = useState('');
  const [search, setSearch] = useState<{ q: string; items: Item[] }>({ q: '', items: [] });
  const itemsById = useRef(new Map<string, Item>());
  const scanWrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let on = true;
    void api().get<Bom>(`/manufacturing/boms/by-item/${itemId}`)
      .then((r) => {
        if (!on) return;
        setExists(true);
        setOutputQty(trimMoney(r.data.outputQuantity));
        setExtraCost(trimMoney(r.data.extraCost));
        setExtraCcy(r.data.extraCostCurrencyCode ?? '');
        setNotes(r.data.notes ?? '');
        setLines(r.data.lines.map((l) => ({
          _key: nextKey(),
          componentItemId: l.componentItemId,
          code: l.componentCode,
          name: l.componentName,
          uom: l.componentUom,
          // Stored per batch in the line's unit ×factor — shown in base units.
          quantity: trimMoney(safeDecimal(l.quantity).times(safeDecimal(l.unitFactor)).toString()),
        })));
      })
      .catch(() => { /* 404 = no recipe yet — the blank editor IS the empty state */ })
      .finally(() => { if (on) setLoaded(true); });
    return () => { on = false; };
  }, [itemId]);

  // Debounced server item search (transfer-form pattern).
  useEffect(() => {
    const q = term.trim();
    if (!q) { setSearch({ q: '', items: [] }); return undefined; }
    let on = true;
    const handle = window.setTimeout(() => {
      void api().get<{ items: Item[] }>('/items', { params: { q, limit: SEARCH_LIMIT } })
        .then((r) => {
          if (!on) return;
          for (const it of r.data.items) itemsById.current.set(it.id, it);
          setSearch({ q, items: r.data.items.filter((it) => it.isActive && it.id !== itemId) });
        })
        .catch(() => { if (on) setSearch({ q, items: [] }); });
    }, SEARCH_DEBOUNCE_MS);
    return () => { on = false; window.clearTimeout(handle); };
  }, [term, itemId]);

  const scanOptions = useMemo<ComboboxOption[]>(
    () => search.items.map((it) => ({
      value: it.id,
      label: `${it.code} — ${it.name}`,
      sublabel: it.barcode ?? undefined,
      keywords: `${it.code} ${it.name} ${it.barcode ?? ''} ${search.q}`,
    })),
    [search],
  );

  const focusScan = (): void => { scanWrapRef.current?.querySelector('input')?.focus(); };

  const addItem = (item: Item, addBase: string): void => {
    if (item.id === itemId) {
      toast.error(t('boms.selfReference', { defaultValue: 'A product cannot be a component of itself.' }));
      return;
    }
    itemsById.current.set(item.id, item);
    setLines((prev) => {
      const existing = prev.find((l) => l.componentItemId === item.id);
      if (existing) {
        return prev.map((l) => (l.componentItemId === item.id
          ? { ...l, quantity: safeDecimal(l.quantity).plus(safeDecimal(addBase)).toString() }
          : l));
      }
      return [...prev, { _key: nextKey(), componentItemId: item.id, code: item.code, name: item.name, uom: item.unitOfMeasure, quantity: trimMoney(addBase) }];
    });
    focusScan();
  };

  const onScanPick = (value: string | undefined): void => {
    if (!value) return;
    setTerm('');
    setSearch({ q: '', items: [] });
    const item = itemsById.current.get(value);
    if (item) addItem(item, '1');
  };

  const resolveScan = async (code: string): Promise<void> => {
    setTerm('');
    setSearch({ q: '', items: [] });
    try {
      const { data } = await api().get<{ item: Item; itemUnitId: string | null }>('/items/resolve', { params: { barcode: code } });
      const unit = data.itemUnitId ? data.item.units.find((u) => u.id === data.itemUnitId) : undefined;
      addItem(data.item, unit ? unit.factor : '1');
    } catch {
      toast.error(t('boms.unknownBarcode', { defaultValue: 'No product found for that barcode.' }));
    }
  };

  const usable = lines.filter((l) => safeDecimal(l.quantity).gt(0));
  const canSave = canWrite && usable.length > 0 && safeDecimal(outputQty).gt(0) && !saving;

  const save = async (): Promise<void> => {
    if (!canSave) return;
    setSaving(true);
    try {
      const body: BomUpsert = {
        outputQuantity: safeDecimal(normalizeNumericInput(outputQty)).toDecimalPlaces(4).toString(),
        extraCost: safeDecimal(normalizeNumericInput(extraCost) || '0').toDecimalPlaces(4).toString(),
        extraCostCurrencyCode: safeDecimal(normalizeNumericInput(extraCost) || '0').gt(0) ? (extraCcy || null) : null,
        isActive: true,
        notes: notes.trim() || null,
        lines: usable.map((l) => ({
          componentItemId: l.componentItemId,
          unitFactor: '1',
          quantity: safeDecimal(l.quantity).toDecimalPlaces(4).toString(),
        })),
      };
      await api().put(`/manufacturing/boms/by-item/${itemId}`, body);
      setExists(true);
      toast.success(t('boms.saved', { defaultValue: 'Recipe saved.' }));
    } catch (err) {
      const code = (err as { response?: { data?: { cause?: { code?: string } } } }).response?.data?.cause?.code;
      toast.error(code === 'production_self_reference'
        ? t('boms.selfReference', { defaultValue: 'A product cannot be a component of itself.' })
        : t('boms.saveFailed', { defaultValue: 'Could not save the recipe.' }));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (): Promise<void> => {
    const ok = await confirmDialog({
      title: t('boms.deleteTitle', { defaultValue: 'Delete this recipe?' }),
      message: t('boms.deleteConfirm', { defaultValue: 'Past production orders keep their own copies — only the recipe itself is removed.' }),
      tone: 'danger',
      confirmLabel: t('boms.delete', { defaultValue: 'Delete recipe' }),
    });
    if (!ok) return;
    try {
      await api().delete(`/manufacturing/boms/by-item/${itemId}`);
      setExists(false);
      setLines([]);
      setOutputQty('1');
      setExtraCost('0');
      setNotes('');
      toast.success(t('boms.deleted', { defaultValue: 'Recipe deleted.' }));
    } catch {
      toast.error(t('boms.saveFailed', { defaultValue: 'Could not save the recipe.' }));
    }
  };

  if (!loaded) return <Card className="p-6"><Skeleton className="h-32 w-full" /></Card>;

  return (
    <div className="flex flex-col gap-4">
      <Card className="p-6">
        <h2 className="mb-1 flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-fg-muted">
          <Factory className="h-4 w-4" aria-hidden />
          {t('boms.title', { defaultValue: 'Recipe (what this product is made of)' })}
        </h2>
        <p className="mb-4 text-sm text-fg-muted">
          {t('boms.hint', { defaultValue: 'The components below are consumed when a production order makes this product. Components stay ordinary products — sellable and purchasable as always.' })}
        </p>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
          <FormField id="bom-out" label={t('boms.outputQty', { defaultValue: 'Batch yield' })} helperText={t('boms.outputQtyHint', { defaultValue: 'How many this recipe makes at once', uom: itemUom })}>
            <NumberInput id="bom-out" className="font-mono" value={outputQty} onValueChange={(v) => setOutputQty(normalizeNumericInput(v))} />
          </FormField>
          <FormField id="bom-extra" label={t('boms.extraCost', { defaultValue: 'Extra cost per run' })} helperText={t('boms.extraCostHint', { defaultValue: 'Labor, electricity, packaging — added to the finished cost' })}>
            <NumberInput id="bom-extra" className="font-mono" value={extraCost} onValueChange={(v) => setExtraCost(normalizeNumericInput(v))} />
          </FormField>
          <FormField id="bom-extra-ccy" label={t('boms.currency', { defaultValue: 'Currency' })}>
            <CurrencySelect id="bom-extra-ccy" value={extraCcy} onChange={setExtraCcy} includeEmpty emptyLabel="—" />
          </FormField>
          <FormField id="bom-notes" label={t('boms.notes', { defaultValue: 'Notes' })}>
            <Input id="bom-notes" value={notes} maxLength={500} onChange={(e) => setNotes(e.target.value)} />
          </FormField>
        </div>
      </Card>

      {canWrite && (
        <div
          ref={scanWrapRef}
          onInput={(e) => setTerm((e.target as HTMLInputElement).value)}
          className="flex items-center gap-2 rounded-lg border border-border bg-bg-subtle px-3 py-2"
        >
          <ScanLine className="h-5 w-5 flex-none text-fg-muted" aria-hidden />
          <div className="flex-1">
            <Combobox
              options={scanOptions}
              onChange={onScanPick}
              onEnterUnmatched={(code) => void resolveScan(code)}
              clearOnSelect
              placeholder={t('boms.scanAdd', { defaultValue: 'Scan a barcode or search to add a component…' })}
              ariaLabel={t('boms.scanAdd', { defaultValue: 'Scan a barcode or search to add a component…' })}
            />
          </div>
        </div>
      )}

      <Card className="overflow-hidden p-0">
        <table className="w-full text-sm">
          <thead className="bg-bg-muted text-fg-subtle">
            <tr>
              <th className="w-10 px-3 py-2 text-start font-medium">#</th>
              <th className="px-3 py-2 text-start font-medium">{t('boms.component', { defaultValue: 'Component' })}</th>
              <th className="w-40 px-3 py-2 text-end font-medium">{t('boms.perBatch', { defaultValue: 'Qty per batch' })}</th>
              {canWrite && <th className="w-12" />}
            </tr>
          </thead>
          <tbody>
            {lines.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-3 py-10 text-center text-sm text-fg-muted">
                  {t('boms.emptyGrid', { defaultValue: 'Scan a barcode or search above to add the first component.' })}
                </td>
              </tr>
            ) : lines.map((l, idx) => (
              <tr key={l._key} className="border-t border-border">
                <td className="px-3 py-2 text-fg-muted">{idx + 1}</td>
                <td className="px-3 py-2">
                  <div className="font-medium">{l.name}</div>
                  <div className="font-mono text-xs text-fg-muted">{l.code} · {l.uom}</div>
                </td>
                <td className="px-3 py-2">
                  <NumberInput
                    className="text-end font-mono"
                    value={l.quantity}
                    disabled={!canWrite}
                    onValueChange={(raw) => {
                      const clean = normalizeNumericInput(raw);
                      setLines((prev) => prev.map((x) => (x._key === l._key ? { ...x, quantity: clean } : x)));
                    }}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); focusScan(); } }}
                    aria-label={t('boms.perBatch', { defaultValue: 'Qty per batch' })}
                  />
                </td>
                {canWrite && (
                  <td className="px-3 py-2 text-end">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      aria-label={t('boms.removeLine', { defaultValue: 'Remove component' })}
                      onClick={() => setLines((prev) => prev.filter((x) => x._key !== l._key))}
                    >
                      <Trash2 className="h-4 w-4" aria-hidden />
                    </Button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {canWrite && (
        <div className="flex items-center justify-end gap-2">
          {exists && (
            <Button type="button" variant="secondary" onClick={() => void remove()}>
              <Trash2 className="me-1.5 h-4 w-4" aria-hidden />
              {t('boms.delete', { defaultValue: 'Delete recipe' })}
            </Button>
          )}
          <Button type="button" disabled={!canSave} loading={saving} onClick={() => void save()}>
            {t('boms.save', { defaultValue: 'Save recipe' })}
          </Button>
        </div>
      )}
    </div>
  );
}
